import { Inject, Injectable, PLATFORM_ID, makeStateKey } from '@angular/core'
import { isPlatformBrowser } from '@angular/common'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { TransferState } from '@angular/core'
import { firstValueFrom } from 'rxjs'

import { environment } from '@app-environments/environment'
import { CookieAuthStorageService } from '@app-services/auth-storage/cookie-auth-storage.service'
import {
    ContentSearchInput,
    ContentSearchResult,
    NEWS_OVERVIEW_QUERY,
    NEWS_SEARCH_QUERY,
    NewsOverviewInput,
    NewsSearchResult,
    PAGE_SEARCH_QUERY,
    PageSearchResult,
} from '@app-domains/meilisearch/types/content-search.types'

type GraphQlPageSearchResponse = { data?: { pageSearch: PageSearchResult }; errors?: Array<{ message: string }> }
type GraphQlNewsSearchResponse = { data?: { newsSearch: NewsSearchResult }; errors?: Array<{ message: string }> }

@Injectable({ providedIn: 'root' })
export class ContentSearchGatewayService {
    constructor(
        private readonly httpClient: HttpClient,
        private readonly transferState: TransferState,
        private readonly authStateStore: CookieAuthStorageService,
        @Inject(PLATFORM_ID) private readonly platformId: object,
    ) {}

    public async search(input: ContentSearchInput): Promise<ContentSearchResult> {
        const cacheKey = makeStateKey<string>(`contentSearch(${JSON.stringify(input)})`)
        const isBrowser = isPlatformBrowser(this.platformId)

        if (! isBrowser && this.transferState.hasKey(cacheKey)) {
            return JSON.parse(this.transferState.get(cacheKey, '{}')) as ContentSearchResult
        }

        const [pageResult, newsResult] = await Promise.all([
            this.fetchPageSearch(input),
            this.fetchNewsSearch(input),
        ])

        const hits = [
            ...pageResult.hits.map((hit) => ({ hit, type: 'page' as const })),
            ...newsResult.hits.map((hit) => ({ hit, type: 'news-item' as const })),
        ]

        const result: ContentSearchResult = {
            hits,
            totalHits: pageResult.totalHits + newsResult.totalHits,
            page: input.page ?? 1,
            perPage: input.perPage ?? 24,
            totalPages: Math.max(pageResult.totalPages, newsResult.totalPages),
            processingTimeMs: pageResult.processingTimeMs + newsResult.processingTimeMs,
        }

        if (! isBrowser) {
            this.transferState.set(cacheKey, JSON.stringify(result))
        }

        return result
    }

    /**
     * News overview listing: newest first, optionally limited to one category.
     */
    public async searchNews(input: NewsOverviewInput): Promise<NewsSearchResult> {
        const cacheKey = makeStateKey<string>(`newsOverview(${JSON.stringify(input)})`)
        const isBrowser = isPlatformBrowser(this.platformId)

        if (isBrowser && this.transferState.hasKey(cacheKey)) {
            const cached = JSON.parse(this.transferState.get(cacheKey, '{}')) as NewsSearchResult
            this.transferState.remove(cacheKey)

            return cached
        }

        const result = await this.post<GraphQlNewsSearchResponse, NewsSearchResult>(
            'NewsOverview',
            NEWS_OVERVIEW_QUERY,
            { input },
            (response) => response.data?.newsSearch,
        )

        if (! isBrowser) {
            this.transferState.set(cacheKey, JSON.stringify(result))
        }

        return result
    }

    private async fetchPageSearch(input: ContentSearchInput): Promise<PageSearchResult> {
        return this.post<GraphQlPageSearchResponse, PageSearchResult>(
            'PageSearch',
            PAGE_SEARCH_QUERY,
            { input },
            (response) => response.data?.pageSearch,
        )
    }

    private async fetchNewsSearch(input: ContentSearchInput): Promise<NewsSearchResult> {
        return this.post<GraphQlNewsSearchResponse, NewsSearchResult>(
            'NewsSearch',
            NEWS_SEARCH_QUERY,
            { input: { query: input.query, locale: input.locale, page: input.page, perPage: input.perPage } },
            (response) => response.data?.newsSearch,
        )
    }

    private async post<TResponse, TResult>(
        operationName: string,
        query: string,
        variables: Record<string, unknown>,
        pick: (response: TResponse) => TResult | undefined,
    ): Promise<TResult> {
        const url = `${environment.api.schema}://${environment.api.hostname}/graphql?op=${operationName}`
        const token = this.authStateStore.getAuthState()?.accessToken
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        })

        const response = await firstValueFrom(this.httpClient.post<TResponse>(url, {
            operationName,
            query,
            variables,
        }, { headers }))

        const errors = (response as { errors?: Array<{ message: string }> }).errors
        if (errors?.length) {
            throw new Error(errors.map((error) => error.message).join('; '))
        }

        const result = pick(response)
        if (! result) {
            throw new Error(`${operationName} returned no data`)
        }

        return result
    }
}
